import React from 'react'
import { Table, Button, Space, Typography, Tooltip, Switch, theme, Popover } from 'antd'
import { useIntl } from 'react-intl'
import { EditOutlined } from '@ant-design/icons'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { FlattenedRateData } from './types'
import htmlToPlainText from '@/components/HtmlToPlainText'
import { HtmlText } from '@/components/HtmlText'
import { SortableRow, DragHandle } from './SortableRow'

const { Text } = Typography

interface RateTabletTableProps {
  rates: FlattenedRateData[]
  loading: boolean
  updatingRateCode: string | null
  isSortMode: boolean
  onEdit: (rateCode: string) => void
  onToggleEnabled: (record: FlattenedRateData, checked: boolean) => void
  onDragEnd: (event: DragEndEvent) => void
}

const RateTabletTable: React.FC<RateTabletTableProps> = ({
  rates,
  loading,
  updatingRateCode,
  isSortMode,
  onEdit,
  onToggleEnabled,
  onDragEnd,
}) => {
  const intl = useIntl()
  const { token } = theme.useToken()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
  )

  const dragHandleColumn = {
    key: 'sort',
    width: 40,
    align: 'center' as const,
    render: () => <DragHandle />,
  }

  const dataColumns = [
    {
      title: 'Rate plan',
      key: 'rate_plan',
      width: 200,
      render: (_: any, record: FlattenedRateData) => (
        <div style={{ display: 'block', flexDirection: 'column', width: 200 }}>
          <Text strong style={{ color: token.colorPrimary }}>{record.rate_code}</Text>
          <br />
          <Text>{record.display_name}</Text>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (html: string) => {
        if (!html) return '-'
        const preview = htmlToPlainText(html)
        return (
          <Popover
            placement="topLeft"
            trigger="click"
            title="Description"
            content={<div style={{ maxWidth: 520, maxHeight: 260, overflow: 'auto' }}><HtmlText value={html} /></div>}
          >
            <div style={{ cursor: 'help', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}>
              {preview || '-'}
            </div>
          </Popover>
        )
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: FlattenedRateData) => (
        <Tooltip title={record.enabled ? 'Deactivate Rate' : 'Activate Rate'}>
          <Switch
            checked={record.enabled}
            onChange={(checked) => onToggleEnabled(record, checked)}
            loading={updatingRateCode === record.rate_code}
            disabled={isSortMode}
            size="small"
            style={{ width: 40 }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: any, record: FlattenedRateData) => (
        <Space size="middle">
          <Tooltip title={intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}>
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={isSortMode}
              onClick={() => onEdit(record.rate_code)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const columns = isSortMode ? [dragHandleColumn, ...dataColumns] : dataColumns

  const table = (
    <Table
      components={isSortMode ? { body: { row: SortableRow } } : undefined}
      columns={columns}
      dataSource={rates}
      loading={loading}
      rowKey="rate_code"
      pagination={false}
      size="small"
      scroll={{ x: 690 }}
    />
  )

  if (!isSortMode) return table

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext
        items={rates.map((r) => r.rate_code)}
        strategy={verticalListSortingStrategy}
      >
        {table}
      </SortableContext>
    </DndContext>
  )
}

export default RateTabletTable
